import { useEffect, useState } from 'react'
import { AlertTriangle, X } from 'lucide-react'
import { isThirdPartyError, isBenignOfflineError } from '../../utils/supportContext'

/**
 * Non-modal toast that surfaces when window catches an uncaught error / rejection.
 * Sentry-style: one toast per session-active error, dismissible. On click —
 * dispatches a CustomEvent('onboardix:support:prefill') that SupportWidget
 * listens for and opens itself with pre-filled summary + description from
 * supportContext.getRecentErrors().
 *
 * Intentionally does NOT auto-create a ticket — explicit user consent (the
 * click) is the trigger. CLAUDE.md §12: program collects the data, user
 * decides to send.
 */

const SESSION_FLAG = '__onboardix_support_autoerr_seen__'

export default function AutoErrorToast() {
    const [visible, setVisible] = useState(false)
    const [message, setMessage] = useState('')

    useEffect(() => {
        if (typeof window === 'undefined') return
        const onError = (event) => {
            if (window[SESSION_FLAG]) return
            // Benign offline-replay is queued & self-healing (SyncStatusBar owns it) —
            // never present it as "Произошла ошибка" or push the user to file a ticket.
            if (isThirdPartyError(event) || isBenignOfflineError(event)) return
            const msg = event?.message || event?.error?.message || event?.reason?.message || 'unhandled error'
            window[SESSION_FLAG] = true
            setMessage(String(msg).slice(0, 200))
            setVisible(true)
        }
        const onRejection = (event) => {
            if (isThirdPartyError(event) || isBenignOfflineError(event)) return
            onError({ message: event?.reason?.message || 'unhandled promise rejection' })
        }
        window.addEventListener('error', onError)
        window.addEventListener('unhandledrejection', onRejection)
        return () => {
            window.removeEventListener('error', onError)
            window.removeEventListener('unhandledrejection', onRejection)
        }
    }, [])

    const _open = () => {
        setVisible(false)
        if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('onboardix:support:prefill', {
                detail: { summary: `[Авто] ${message}`, fromAutoError: true },
            }))
        }
    }

    const _dismiss = () => setVisible(false)

    if (!visible) return null

    return (
        <div
            role="alert"
            style={{
                position: 'fixed',
                bottom: 92,
                right: 24,
                width: 340,
                maxWidth: 'calc(100vw - 32px)',
                padding: 12,
                background: 'var(--color-bg-elevated)',
                border: '1px solid var(--color-danger)',
                borderRadius: 12,
                boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
                zIndex: 9999,
                display: 'flex',
                gap: 10,
                alignItems: 'flex-start',
            }}
        >
            <AlertTriangle size={20} style={{ color: 'var(--color-danger)', flexShrink: 0, marginTop: 2 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>Произошла ошибка</div>
                <div style={{ fontSize: 12, color: 'var(--color-text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {message}
                </div>
                <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
                    <button type="button" className="btn btn--primary btn--sm" onClick={_open} style={{ fontSize: 12, padding: '4px 10px' }}>
                        Отправить в поддержку
                    </button>
                    <button type="button" className="btn btn--secondary btn--sm" onClick={_dismiss} style={{ fontSize: 12, padding: '4px 10px' }}>
                        Пропустить
                    </button>
                </div>
            </div>
            <button type="button" onClick={_dismiss} aria-label="Закрыть" className="btn btn--icon btn--sm">
                <X size={16} />
            </button>
        </div>
    )
}
